import { useCallback, useEffect, useState } from 'react';
import { useMyOrg } from 'src/shared/api';
import type { CesiumMouseLocationCoordinates } from 'src/shared/cesium';
import { CoordinateSystem, EPSGCode } from 'src/shared/enums';
import { getUTMCoordinates } from 'src/shared/helpers';
import { useAppSelector } from '../../../app/hooks';
import {
  selectMap3DState,
  selectMap3dDataset,
} from '../../shared/map-3d-slices';
import { isNil } from 'lodash';

const Map3DFooter = () => {
  // States.
  const [mouseLocationString, setMouseLocationString] = useState('');
  const [coordinateSystem, setCoordinateSystem] = useState<CoordinateSystem>(
    CoordinateSystem.WGS84,
  );

  // Selectors.
  const { selectedTerrainSite } = useAppSelector(selectMap3dDataset);

  // APIs.
  const { data: organisationResponse, isSuccess: isSuccessOrganisation } =
    useMyOrg();

  // Hooks.
  const { cesiumProxy } = useAppSelector(selectMap3DState);

  // Listeners.
  const mouseLocationUpdateListener = useCallback(
    (coordinates: CesiumMouseLocationCoordinates) => {
      setMouseLocationString(
        formatLocationString(coordinates, coordinateSystem),
      );
    },
    [coordinateSystem],
  );

  // useEffect - Mount.
  useEffect(() => {
    if (!isNil(cesiumProxy)) {
      cesiumProxy.addMouseLocationListener(mouseLocationUpdateListener);

      return () => {
        cesiumProxy.removeMouseLocationListener(mouseLocationUpdateListener);
      };
    }
  }, [cesiumProxy, mouseLocationUpdateListener]);

  useEffect(() => {
    if (
      isSuccessOrganisation &&
      organisationResponse?.data.settings?.coordinateSystem
    ) {
      setCoordinateSystem(
        organisationResponse?.data.settings?.coordinateSystem,
      );
    }
  }, [isSuccessOrganisation, organisationResponse]);

  // Helpers.
  const formatLocationString = (
    coordinates: CesiumMouseLocationCoordinates,
    coordinateSystem: CoordinateSystem,
  ) => {
    const { longitude, latitude, altitude } = coordinates;

    if (coordinateSystem === CoordinateSystem.UTM) {
      const { zoneNumber, hemisphere, utmCoords } = getUTMCoordinates(
        longitude,
        latitude,
        EPSGCode.WGS84,
      );

      if (zoneNumber === -1) {
        return `- | - | ALT ${altitude} m | ${coordinateSystem} -`;
      }

      return `${coordinateSystem} ${zoneNumber}${hemisphere} | ${utmCoords.easting} m E | ${utmCoords.northing} m N | ALT ${altitude} m`;
    }

    return `${coordinateSystem} | LAT ${latitude} | LONG ${longitude} | ALT ${altitude} m`;
  };

  return (
    <footer className="map-3d-container__bottom-bar">
      <div className="map-3d-container__bottom-bar-company">AUS Pvt Ltd</div>
      <div className="map-3d-container__bottom-bar-site-name">
        {selectedTerrainSite?.name}
      </div>
      <div className="map-3d-container__bottom-bar-mouse-location">
        {mouseLocationString}
      </div>
    </footer>
  );
};

export default Map3DFooter;
